import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CopySimple, ArrowsIn, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * §8b — Duplicate detection + merge action.
 *
 * Shown on the ticket detail sidebar. Fetches candidates from
 * /api/tickets/{id}/duplicates (same requester + fuzzy subject match)
 * and lets the agent merge them into the current ticket — with a
 * dry-run preview modal so the blast radius is visible before commit.
 */
export function DuplicatesCard({ ticketId, ticketNumber, onMerged }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { candidate, data }
  const [merging, setMerging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tickets/${ticketId}/duplicates`);
      setCandidates(r.data.candidates || []);
    } catch (e) {
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const openPreview = async (c) => {
    // Enforce policy: original (lower-numbered) ticket always survives.
    const currentIsOlder = ticketNumber < c.number;
    const primaryId = currentIsOlder ? ticketId : c.id;
    const duplicateId = currentIsOlder ? c.id : ticketId;
    try {
      const r = await api.post("/tickets/merge-preview", {
        primary_id: primaryId,
        duplicate_ids: [duplicateId],
      });
      setPreview({
        candidate: c,
        data: r.data,
        // Reuse the manual-merge plumbing for direction — it already
        // knows how to handle the current-ticket-is-duplicate case,
        // including navigating to the survivor after confirm.
        manual: {
          direction: currentIsOlder ? "that-into-this" : "this-into-that",
          primaryId,
          duplicateId,
          primaryNumber: currentIsOlder ? ticketNumber : c.number,
          duplicateNumber: currentIsOlder ? c.number : ticketNumber,
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const confirmMerge = async () => {
    if (!preview) return;
    setMerging(true);
    try {
      const r = await api.post("/tickets/merge", {
        primary_id: ticketId,
        duplicate_ids: [preview.candidate.id],
      });
      toast.success(`Merged #${preview.candidate.number} into this ticket`);
      onMerged?.(r.data);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  // Manual-merge state — always available, regardless of auto-detection.
  // Auto-detection filters on same requester_email, which misses cases
  // where the two tickets have different requesters (e.g. IT Support
  // forwarding an end-user's request as a new email). This entry point
  // lets an agent point at any ticket by number.
  const [manualNumber, setManualNumber] = useState("");
  const [manualBusy, setManualBusy] = useState(false);

  const _fetchByNumber = async (num) => {
    const r = await api.get(`/tickets/lookup-by-number/${num}`);
    return r.data;  // { id, number, subject, status, requester_email }
  };

  const _openManualPreview = async ({ primaryId, duplicateId, otherNumber, otherId, direction }) => {
    setManualBusy(true);
    try {
      const r = await api.post("/tickets/merge-preview", {
        primary_id: primaryId,
        duplicate_ids: [duplicateId],
      });
      // For "this into that", the survivor is the OTHER ticket, and the
      // preview modal expects `primaryNumber` to describe the survivor
      // (the ticket the content lands on). Stash direction + otherNumber
      // so confirm() and the dialog can reason about it.
      setPreview({
        candidate: {
          id: direction === "this-into-that" ? duplicateId : otherId,
          number: direction === "this-into-that" ? ticketNumber : otherNumber,
        },
        data: r.data,
        manual: {
          direction,
          primaryId,
          duplicateId,
          primaryNumber: direction === "this-into-that" ? otherNumber : ticketNumber,
          duplicateNumber: direction === "this-into-that" ? ticketNumber : otherNumber,
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setManualBusy(false);
    }
  };

  const openManual = async () => {
    const raw = manualNumber.trim().replace(/^#/, "");
    const num = Number(raw);
    if (!Number.isInteger(num) || num <= 0) {
      toast.error("Enter a ticket number");
      return;
    }
    if (num === ticketNumber) {
      toast.error("Can't merge a ticket with itself");
      return;
    }
    let other;
    try {
      other = await _fetchByNumber(num);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Ticket #${num} not found`);
      return;
    }
    // Direction is fixed policy: the ORIGINAL (older / lower-numbered)
    // ticket always survives so the requester keeps seeing the number
    // they've been corresponding on. Ticket numbers are a monotonic
    // counter so lower = older.
    const currentIsOlder = ticketNumber < other.number;
    if (currentIsOlder) {
      // Other is the newer duplicate → fold it into current.
      await _openManualPreview({
        primaryId: ticketId,
        duplicateId: other.id,
        otherNumber: other.number,
        otherId: other.id,
        direction: "that-into-this",
      });
    } else {
      // Current is the newer duplicate → move it into the older other.
      await _openManualPreview({
        primaryId: other.id,
        duplicateId: ticketId,
        otherNumber: other.number,
        otherId: other.id,
        direction: "this-into-that",
      });
    }
  };

  const confirmManualMerge = async () => {
    if (!preview?.manual) return confirmMerge();
    setMerging(true);
    try {
      const r = await api.post("/tickets/merge", {
        primary_id: preview.manual.primaryId,
        duplicate_ids: [preview.manual.duplicateId],
      });
      toast.success(
        `Merged #${preview.manual.duplicateNumber} into #${preview.manual.primaryNumber}`
      );
      onMerged?.(r.data);
      setPreview(null);
      setManualNumber("");
      // If the current ticket was the DUPLICATE, navigate to the survivor.
      if (preview.manual.direction === "this-into-that") {
        window.location.href = `/tickets/${preview.manual.primaryId}`;
      } else {
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  if (loading) return null;

  return (
    <>
      {candidates.length > 0 && (
      <div
        className="hairline rounded-lg p-3 bg-white space-y-2"
        data-testid="duplicates-card"
      >
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium flex items-center gap-1">
          <CopySimple size={11} weight="duotone" /> Possible duplicates ({candidates.length})
        </div>
        <div className="space-y-1.5">
          {candidates.map((c) => (
            <div
              key={c.id}
              data-testid={`duplicate-candidate-${c.number}`}
              className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/tickets/${c.id}`}
                  className="text-xs font-mono text-slate-600 hover:text-[#4f4c90]"
                >
                  #{c.number}
                </Link>
                <div className="text-xs text-slate-800 truncate">{c.subject}</div>
                <div className="text-[10px] text-slate-500">
                  {Math.round(c.similarity * 100)}% match · status: {c.status}
                </div>
              </div>
              <Button
                type="button"
                onClick={() => openPreview(c)}
                data-testid={`duplicate-merge-${c.number}`}
                className="h-7 px-2 text-[11px] bg-[#4f4c90] hover:bg-[#5F5CA5] text-white gap-1"
              >
                <ArrowsIn size={11} /> Review merge
              </Button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Manual merge — always available, no auto-detection dependency */}
      <div
        className="hairline rounded-lg p-3 bg-white space-y-2 mt-2"
        data-testid="manual-merge-card"
      >
        <div className="text-[11px] uppercase text-slate-500 tracking-wider font-medium flex items-center gap-1">
          <ArrowsIn size={11} weight="duotone" /> Merge with another ticket
        </div>
        <div className="text-[11px] text-slate-600">
          The <span className="font-medium">original (lower-numbered)</span> ticket always survives so the requester keeps seeing the same number in their inbox.
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-mono text-slate-500">#</span>
          <input
            type="text"
            inputMode="numeric"
            value={manualNumber}
            onChange={(e) => setManualNumber(e.target.value)}
            placeholder="e.g. 448"
            className="flex-1 min-w-0 border border-slate-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#4f4c90]"
            data-testid="manual-merge-input"
          />
        </div>
        <Button
          type="button"
          disabled={manualBusy || !manualNumber.trim()}
          onClick={openManual}
          data-testid="manual-merge-submit"
          className="h-7 w-full text-[11px] bg-[#4f4c90] hover:bg-[#5F5CA5] text-white gap-1 px-2"
        >
          {(() => {
            const raw = manualNumber.trim().replace(/^#/, "");
            const num = Number(raw);
            if (!Number.isInteger(num) || num <= 0) return "Merge…";
            if (num === ticketNumber) return "Choose a different ticket";
            const currentIsOlder = ticketNumber < num;
            return currentIsOlder
              ? `Fold #${num} into #${ticketNumber}`
              : `Move #${ticketNumber} into #${num}`;
          })()}
        </Button>
      </div>

      {preview && (
        <MergePreviewDialog
          preview={preview}
          onCancel={() => setPreview(null)}
          onConfirm={preview.manual ? confirmManualMerge : confirmMerge}
          merging={merging}
          primaryNumber={preview.manual ? preview.manual.primaryNumber : ticketNumber}
          duplicateNumber={preview.manual ? preview.manual.duplicateNumber : preview.candidate.number}
        />
      )}
    </>
  );
}


function MergePreviewDialog({ preview, onCancel, onConfirm, merging, primaryNumber, duplicateNumber }) {
  const { data, candidate } = preview;
  const t = data.totals;
  const dup = data.duplicates[0] || {};
  const dupNum = duplicateNumber ?? candidate.number;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      data-testid="merge-preview-dialog"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Preview merge</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Merging <span className="font-mono">#{dupNum}</span> into <span className="font-mono">#{primaryNumber}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="merge-preview-close"
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
          <div className="font-medium text-amber-900 mb-1">Duplicate to be merged</div>
          <div className="text-slate-800">
            <span className="font-mono text-slate-500">#{dup.number}</span> · {dup.subject}
          </div>
          <div className="text-slate-500 text-[11px] mt-0.5">
            {dup.requester_email} · status: {dup.status}
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">
            What will move to #{primaryNumber}
          </div>
          <ul className="text-sm text-slate-800 space-y-1.5">
            <li className="flex justify-between border-b border-slate-100 py-1" data-testid="merge-preview-comments">
              <span>Comments</span>
              <span className="font-mono tabular-nums text-slate-900">{t.comments_moving}</span>
            </li>
            <li className="flex justify-between border-b border-slate-100 py-1" data-testid="merge-preview-attachments">
              <span>Attachments</span>
              <span className="font-mono tabular-nums text-slate-900">{t.attachments_moving}</span>
            </li>
            <li className="flex justify-between border-b border-slate-100 py-1" data-testid="merge-preview-activity">
              <span>Activity entries</span>
              <span className="font-mono tabular-nums text-slate-900">{t.activity_moving}</span>
            </li>
            <li className="flex justify-between border-b border-slate-100 py-1" data-testid="merge-preview-threading">
              <span>Email threading references</span>
              <span className="font-mono tabular-nums text-slate-900">{t.message_ids_being_added}</span>
            </li>
            <li className="flex justify-between py-1" data-testid="merge-preview-ccs">
              <span>New CC recipients</span>
              <span className="font-mono tabular-nums text-slate-900">{(t.cc_emails_being_added || []).length}</span>
            </li>
          </ul>
          {(t.cc_emails_being_added || []).length > 0 && (
            <div className="mt-2 text-[11px] text-slate-500">
              CCs joining: {t.cc_emails_being_added.join(", ")}
            </div>
          )}
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600">
          After merge, <span className="font-mono">#{dup.number}</span> will be soft-deleted (recoverable
          from the Recycle Bin) with a `merged_into_id` pointing at{" "}
          <span className="font-mono">#{primaryNumber}</span>. Future replies to the old thread will
          land on <span className="font-mono">#{primaryNumber}</span>.
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={merging}
            data-testid="merge-preview-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={merging}
            data-testid="merge-preview-confirm"
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] text-white gap-1"
          >
            <ArrowsIn size={13} /> {merging ? "Merging…" : "Confirm merge"}
          </Button>
        </div>
      </div>
    </div>
  );
}

